// The access challenge (WEBSITE feature requirements 26 "workspace chooser
// where applicable", api-surface 1.4 and 2.0). A password sign-in can answer
// 409 tenant_selection_required or 428 bootstrap_password_replacement_required,
// and the Discord callback can land with the matching statuses. Both hold a
// short-lived aleph_access cookie; this screen reads the challenge and
// finishes it, so neither answer is a dead end.
//
// The workspace chooser selects first and opens with the one primary of this
// view; the bootstrap flow replaces the temporary owner password. Cancel
// releases the challenge cookie and returns to the door.
import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccessChallenge } from '../../api/hooks';
import { postAccessChallengeCancel, postAccessChallengePassword, postAccessChallengeWorkspace } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import { isApiError } from '../../api/errors';
import type { AccessChallenge as AccessChallengeState, Workspace } from '../../api/types';
import { useElapsedLine } from '../../states/useElapsed';
import { HonestState } from '../../states/HonestState';
import { stateOfError } from '../../states/stateOf';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Input';
import { formatZurichDayTime } from '../../time/zurich';
import { AuthFrame } from './AuthFrame';
import { PASSWORD_MIN_LENGTH } from './InvitationAccept';

export interface AccessChallengeProps {
  // What the door already knows, used only for the title while the read runs.
  readonly expected: 'workspace' | 'password' | null;
  readonly onCancel: () => void;
}

function titleFor(purpose: string | undefined, expected: AccessChallengeProps['expected']): string {
  if (purpose === 'tenant_selection' || (purpose === undefined && expected === 'workspace')) return 'Choose a workspace';
  if (purpose === 'bootstrap_password_replacement' || (purpose === undefined && expected === 'password')) return 'Replace the temporary password';
  return 'One more step';
}

function expiryLine(challenge: AccessChallengeState): string {
  const at = formatZurichDayTime(challenge.expiresAt);
  return at ? `This step expires at ${at}.` : 'This step expires shortly.';
}

function WorkspaceChooser({ challenge, onCancel }: { readonly challenge: AccessChallengeState; readonly onCancel: () => void }) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(challenge.workspaces[0]?.id ?? null);
  const open = useMutation({
    mutationFn: postAccessChallengeWorkspace,
    onSuccess: (identity) => {
      queryClient.setQueryData(queryKeys.authMe(), identity);
      void queryClient.invalidateQueries({ queryKey: queryKeys.authMe() });
    },
  });

  if (challenge.workspaces.length === 0) {
    return (
      <HonestState
        variant="Permission denied"
        title="No workspace to open"
        reason="The sign-in asked for a workspace choice, but the challenge listed none. The invitation may have been revoked between the two requests."
        nextAction={{ label: 'Back to sign in', onSelect: onCancel }}
      />
    );
  }

  const rows = challenge.workspaces.map((workspace: Workspace) => (
    <label className="door-choice" key={workspace.id}>
      <input
        type="radio"
        name="workspace"
        value={workspace.id}
        checked={selected === workspace.id}
        onChange={() => setSelected(workspace.id)}
      />
      <span className="door-choice-nm">{workspace.name}</span>
      <span className="door-choice-role">{workspace.role}</span>
    </label>
  ));

  return (
    <form
      className="door-form"
      aria-label="Choose a workspace"
      onSubmit={(event: FormEvent<HTMLFormElement>) => { event.preventDefault(); if (selected) open.mutate(selected); }}
    >
      <p className="door-note">This identity belongs to {challenge.workspaces.length} workspaces. {expiryLine(challenge)}</p>
      <div className="door-choices" role="radiogroup" aria-label="Workspaces">{rows}</div>
      <div className="door-row">
        <Button variant="primary" type="submit" disabled={open.isPending || selected === null}>{open.isPending ? 'Opening' : 'Open workspace'}</Button>
        <Button variant="tertiary" onClick={onCancel}>Cancel</Button>
      </div>
      {open.isError ? (
        <HonestState
          variant={stateOfError(open.error).variant}
          title="Workspace not opened"
          reason={isApiError(open.error) && open.error.status === 403
            ? 'The challenge is no longer valid (it expired or was already used). Sign in again to get a new one.'
            : stateOfError(open.error).reason}
          nextAction={[{ label: 'Try again', onSelect: () => open.reset() }, { label: 'Back to sign in', onSelect: onCancel }]}
        />
      ) : null}
    </form>
  );
}

function BootstrapPassword({ challenge, onCancel }: { readonly challenge: AccessChallengeState; readonly onCancel: () => void }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const replace = useMutation({
    mutationFn: postAccessChallengePassword,
    onSuccess: (answer) => {
      if (!answer.signInRequired) void queryClient.invalidateQueries({ queryKey: queryKeys.authMe() });
    },
  });
  const tooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
  const mismatch = again.length > 0 && again !== password;

  if (replace.isSuccess && replace.data.signInRequired) {
    return (
      <HonestState
        variant="Ready"
        title="Password replaced"
        reason="The temporary owner password is gone. Sign in with the new one to open the workspace."
        nextAction={{ label: 'Go to sign in', onSelect: onCancel }}
      />
    );
  }

  return (
    <form
      className="door-form"
      aria-label="Replace the temporary password"
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (password.length < PASSWORD_MIN_LENGTH || password !== again) return;
        replace.mutate(password);
      }}
    >
      <p className="door-note">The workspace was created with a temporary owner password. Replace it before the workspace opens. {expiryLine(challenge)}</p>
      <label className="door-lbl" htmlFor="challenge-new">New password, {PASSWORD_MIN_LENGTH} characters or more</label>
      <Field id="challenge-new" name="password" type="password" revealable autoComplete="new-password" value={password} invalid={tooShort} onChange={(event) => setPassword(event.target.value)} required minLength={PASSWORD_MIN_LENGTH} />
      <label className="door-lbl" htmlFor="challenge-again">Repeat it</label>
      <Field id="challenge-again" name="passwordAgain" type="password" revealable autoComplete="new-password" value={again} invalid={mismatch} onChange={(event) => setAgain(event.target.value)} required />
      {tooShort ? <p className="door-note">{PASSWORD_MIN_LENGTH - password.length} more characters needed.</p> : null}
      {mismatch ? <p className="door-note">The two entries differ.</p> : null}
      <div className="door-row">
        <Button variant="primary" type="submit" disabled={replace.isPending}>{replace.isPending ? 'Saving' : 'Set password'}</Button>
        <Button variant="tertiary" onClick={onCancel}>Cancel</Button>
      </div>
      {replace.isError ? (
        <HonestState
          variant={stateOfError(replace.error).variant}
          title="Password not replaced"
          reason={isApiError(replace.error) && replace.error.status === 400
            ? 'The workspace rejected that password. It must differ from the temporary one and meet the length rule.'
            : stateOfError(replace.error).reason}
          nextAction={[{ label: 'Try again', onSelect: () => replace.reset() }, { label: 'Back to sign in', onSelect: onCancel }]}
        />
      ) : null}
    </form>
  );
}

export function AccessChallenge({ expected, onCancel }: AccessChallengeProps) {
  const challenge = useAccessChallenge(true);
  const cancel = useMutation({ mutationFn: postAccessChallengeCancel });
  const elapsed = useElapsedLine(challenge.isPending);

  const dismiss = () => {
    cancel.mutate(undefined, { onSettled: onCancel });
  };

  const purpose = challenge.data?.purpose;
  return (
    <AuthFrame
      title={titleFor(typeof purpose === 'string' ? purpose : undefined, expected)}
      foot="This step uses a short-lived access cookie, separate from the session cookie. Cancelling releases it and returns you to the door."
    >
      {challenge.isPending ? (
        <HonestState variant="Loading" reason="Reading the access challenge (15 s limit)." detail={elapsed} nextAction={{ label: 'Cancel', onSelect: dismiss }} />
      ) : null}
      {challenge.isError ? (
        <HonestState
          variant={isApiError(challenge.error) && (challenge.error.status === 401 || challenge.error.status === 403) ? 'Authentication required' : stateOfError(challenge.error).variant}
          title="Access challenge unavailable"
          reason={isApiError(challenge.error) && (challenge.error.status === 401 || challenge.error.status === 403)
            ? 'The challenge cookie is missing or expired, so this step cannot be finished. Sign in again to start a new one.'
            : stateOfError(challenge.error).reason}
          nextAction={[{ label: 'Retry', onSelect: () => challenge.refetch() }, { label: 'Back to sign in', onSelect: dismiss }]}
        />
      ) : null}
      {challenge.data && challenge.data.purpose === 'bootstrap_password_replacement'
        ? <BootstrapPassword challenge={challenge.data} onCancel={dismiss} />
        : null}
      {challenge.data && challenge.data.purpose !== 'bootstrap_password_replacement'
        ? <WorkspaceChooser challenge={challenge.data} onCancel={dismiss} />
        : null}
    </AuthFrame>
  );
}
